
  import { computed, defineComponent, inject, Ref, ref, watch } from 'vue';
  import { Loader } from '@/ui/components/Loader';
  import { Box } from '@/ui/components/Box';
  import { InputField } from '@/ui/components/form/InputField';
  import { ResourceTable } from '@/ui/components/table/ResourceTable';
  import { du } from '@/ui/i18n/i18n';
  import { Tag } from '@/ui/components/Tag';
  import { openNewRouterTab, toFixed } from '@/ui/helpers';
  import StaticPagination from '@/ui/components/StaticPagination.vue';
  import { issuePriority, issueStatusToText } from '@/ui/dictionary';
  import { useIssue } from '@/ui/concerns/issue/useIssue';
  import { useDetailedView, useDetailedViewFilters } from '@/ui/concerns/analytics/useDetailedView';
  import { useDetailedViewStore } from '@/stores/analytics/detailedView';
  import { ReportRecordData } from '@/services/api/resources/analytics-builder/model';
  import { reportFromAnalyticsPageKey } from '@/ui/injectionKeys';

  export default defineComponent({
    components: {
      Loader,
      Box,
      InputField,
      ResourceTable,
      Tag,
      Pagination: StaticPagination,
    },
    setup() {
      const { getDetailedViewDataTask, data, search, page, perPage } = useDetailedView();
      const { prepareFilters } = useDetailedViewFilters();
      const store = useDetailedViewStore();
      const report = inject<Ref<ReportRecordData | null>>(reportFromAnalyticsPageKey, ref(null));

      const widget = computed(() => report.value?.widgets.find(widget => widget.key === store.widgetKey));
      const combinedFilters = computed(() => prepareFilters(
        widget.value!.filters,
        report.value!.filters,
        widget.value!.attribute,
        store.resourceId,
        store.options,
      ));
      const taskParams = computed(() => ({
        filters: combinedFilters.value,
        metric: widget.value!.metric,
        attribute: widget.value!.attribute,
        page: page.value,
        perPage,
        searchTerm: search.value,
      }));

      const onPageChanged = (value: number) => {
        page.value = value;
      };

      const updateSearch = (value: string) => {
        search.value = value;
      };

      watch([page, search], () => getDetailedViewDataTask.value.run(taskParams.value));

      const onRowClick = ({ id }: { id: string }) => {
        openNewRouterTab({ name: 'issueEdit', params: { id } });
      };

      const { priorityToTagType, issueStatusToTagType } = useIssue();

      const getAssignees = (item: { user_groups: Array<{ value: string }>; assignees: Array<{ value: string }> }) => [
        // TODO: to be added external associates when the backend is ready
        ...item.user_groups.map(group => group.value),
        ...item.assignees.map(assignee => assignee.value),
      ].join(', ');

      const hasAssignees = (item: { user_groups: []; assignees: [] }) => {
        // TODO: to be checked external associates when the backend is ready
        return item.user_groups.length > 0 || item.assignees.length > 0;
      };

      return {
        search,
        page,
        perPage,
        onPageChanged,
        updateSearch,
        getDetailedViewDataTask,
        taskParams,
        data,
        issueStatusToTagType,
        priorityToTagType,
        issuePriority,
        issueStatusToText,
        toFixed,
        du,
        getAssignees,
        hasAssignees,
        onRowClick,
      };
    },
  });
